import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Building2, Inbox, Mail, Clock, CheckCircle2, XCircle, AlertCircle, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InviteAcceptPublic() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const action = params.get('action'); // accept | decline | null
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, invitation: null, error: null });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/company-invitations/resolve-token/${token}`);
        const data = await r.json();
        if (cancel) return;
        if (!r.ok) {
          setState({ loading: false, invitation: null, error: data.detail || 'Invalid invitation link' });
        } else {
          setState({ loading: false, invitation: data, error: null });
        }
      } catch (e) {
        if (!cancel) setState({ loading: false, invitation: null, error: 'Network error' });
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const goToInbox = () => {
    // Carry intent through login → after login, deep-link to the building invitations inbox
    const next = '/agent/building-invitations';
    const tokenStored = localStorage.getItem('session_token');
    if (tokenStored) navigate(next);
    else navigate(`/login?next=${encodeURIComponent(next)}`);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 flex items-center gap-2" data-testid="invite-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading invitation…
        </div>
      </div>
    );
  }

  if (state.error || !state.invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full" data-testid="invite-error">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold mb-2">Invitation unavailable</h2>
            <p className="text-gray-600 text-sm mb-4">{typeof state.error === 'string' ? state.error : 'This invitation link is invalid or has expired.'}</p>
            <Button onClick={() => navigate('/')}>Back to BodyCorp365</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const inv = state.invitation;
  const isFinal = ['accepted', 'declined', 'expired'].includes(inv.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-xl w-full" data-testid="invite-public-card">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Building2 className="w-7 h-7 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Building Invitation</h1>
              <p className="text-sm text-gray-600">via BodyCorp365</p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold text-base mb-1">{inv.building_name}</div>
              <div className="text-gray-600">
                <Mail className="inline w-4 h-4 mr-1" />
                Invited by <strong>{inv.invited_by_email}</strong>
              </div>
              {inv.message && (
                <blockquote className="mt-3 pl-3 border-l-2 border-indigo-300 text-gray-700">
                  "{inv.message}"
                </blockquote>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-500">Status</span>
              <Badge variant="outline" className={`capitalize ${
                inv.status === 'accepted' ? 'bg-green-100 text-green-800' :
                inv.status === 'declined' ? 'bg-red-100 text-red-800' :
                inv.status === 'expired' ? 'bg-gray-200 text-gray-700' :
                'bg-blue-100 text-blue-800'
              }`}>
                {inv.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                {inv.status === 'viewed' && <AlertCircle className="w-3 h-3 mr-1" />}
                {inv.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                {inv.status === 'declined' && <XCircle className="w-3 h-3 mr-1" />}
                {inv.status}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Sent to</span>
              <span>{inv.contact_email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Expires</span>
              <span>{new Date(inv.expires_at).toLocaleString()}</span>
            </div>
            {inv.is_replacement && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                ⚠️ This is a replacement invitation — accepting will end the current management assignment.
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2">
            {isFinal ? (
              <div className="text-center text-gray-600 text-sm">
                This invitation is already <strong>{inv.status}</strong>. No further action needed.
              </div>
            ) : (
              <>
                <Button onClick={goToInbox} className="w-full" data-testid="open-inbox-btn">
                  <Inbox className="w-4 h-4 mr-2" />
                  {action === 'accept' ? 'Sign in to Accept' :
                   action === 'decline' ? 'Sign in to Decline' :
                   'Open in Building Invitations Inbox'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  You'll be asked to sign in (or register) before accepting on behalf of your company.
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
